<script lang="ts">
  import { getSeries, getGenresTags, getLibraries } from '$lib/apis/library/api'
  import HomeBookshelfSection from '$lib/components/nuread/HomeBookshelfSection.svelte'
  import HomeTagSection from '$lib/components/nuread/HomeTagSection.svelte'
  import ResumeHero from '$lib/components/nuread/ResumeHero.svelte'
  import WelcomeStrip from '$lib/components/nuread/WelcomeStrip.svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import Library from '@lucide/svelte/icons/library'
  import Plus from '@lucide/svelte/icons/plus'
  import { servers } from '$lib/store'
  import { seriesDataEpoch } from '$lib/operations.svelte'
  import { authState, getIdentity } from '$lib/auth.svelte'
  import { filterNsfwSeries, prefs } from '$lib/preferences.svelte'
  import { createLibraryModal } from '$lib/create-library.svelte'

  let { route } = $props()

  const genresTags = $state({
    tags: [] as string[],
    genres: [] as string[],
  })

  const serverid = $derived(route.result.path.params.mediaid)

  let series = $state({
    added: [] as any[],
    continue: [] as any[],
    score: [] as any[],
  })
  let seriesLoading = $state(true)
  /** Full catalog across libraries (not limited like the home feed). */
  let catalog = $state<any[]>([])
  /** null until first libraries fetch settles — avoids flashing Empty. */
  let libraryCount = $state<number | null>(null)

  const kindAccent: Record<string, 'blue' | 'peach' | 'sky' | 'mauve' | 'green'> = {
    MANGA: 'mauve',
    NOVEL: 'blue',
    WEBTOON: 'sky',
    WEBNOVEL: 'peach',
  }

  $effect(() => {
    void getIdentity()
  })

  $effect(() => {
    void serverid
    libraryCount = null
  })

  $effect(() => {
    void seriesDataEpoch.value
    const srv = $servers[serverid]
    if (!srv) return
    let cancelled = false
    seriesLoading = true

    const feed = Promise.all([
      getSeries(srv, 'all', 'added').then(r => {
        if (!cancelled) series.added = r?.Series ?? []
      }),
      getSeries(srv, 'all', 'continue').then(r => {
        if (!cancelled) series.continue = r?.Series ?? []
      }),
      getSeries(srv, 'all', 'score').then(r => {
        if (!cancelled) series.score = r?.Series ?? []
      }),
    ]).finally(() => {
      if (!cancelled) seriesLoading = false
    })

    getGenresTags(srv).then(r => {
      if (cancelled) return
      genresTags.tags = r?.Tags ?? []
      genresTags.genres = r?.Genres ?? []
    })

    getLibraries(srv).then(async r => {
      const libs = r?.Libraries ?? []
      if (!cancelled) libraryCount = libs.length
      const pages = await Promise.all(
        libs.map((lib: any) => getSeries(srv, lib.id, 'added'))
      )
      if (cancelled) return
      const byId = new Map<string, any>()
      for (const page of pages) {
        for (const s of page?.Series ?? []) {
          if (s?.id) byId.set(s.id, s)
        }
      }
      catalog = [...byId.values()]
    })

    return () => {
      cancelled = true
      void feed
    }
  })

  // After Create Library closes, refresh count so Empty clears before scan finishes.
  $effect(() => {
    if (createLibraryModal.open) return
    if (createLibraryModal.serverid !== serverid) return
    const srv = $servers[serverid]
    if (!srv) return
    getLibraries(srv).then(r => {
      libraryCount = r?.Libraries?.length ?? 0
    })
  })

  const continueSeries = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series.continue)
  })
  const addedSeries = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series.added)
  })
  const visibleCatalog = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(catalog)
  })

  const resumeSeries = $derived(
    continueSeries[0]
      ? { ...continueSeries[0], mediaId: serverid }
      : undefined
  )

  const userName = $derived(
    authState.name && authState.name !== 'Not Connected'
      ? authState.name
      : 'Reader'
  )

  const stripKindStats = $derived.by(() => {
    const byKind: Record<string, number> = {}
    for (const s of visibleCatalog) {
      const kind = String(s?.kind ?? '').toUpperCase()
      if (kind) byKind[kind] = (byKind[kind] ?? 0) + 1
    }
    return Object.entries(byKind)
      .filter(([, value]) => value > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([kind, value]) => ({
        label: kind.toLowerCase(),
        value,
        accent: (kindAccent[kind] ?? 'blue') as
          | 'blue'
          | 'peach'
          | 'sky'
          | 'mauve'
          | 'green',
      }))
  })

  const stripProgressStats = $derived.by(() => {
    let completedSeries = 0
    let inProgressSeries = 0
    for (const s of visibleCatalog) {
      const done = Number(s?.Progress?.Completed ?? 0)
      const total = Number(s?.Progress?.Total ?? 0)
      if (total > 0 && done >= total) completedSeries += 1
      else if (done > 0) inProgressSeries += 1
    }
    return [
      { label: 'in progress', value: inProgressSeries, accent: 'green' as const },
      { label: 'completed', value: completedSeries, accent: 'sky' as const },
    ]
  })

  const isEmpty = $derived(libraryCount === 0)

  function openCreateLibrary() {
    createLibraryModal.serverid = serverid
    createLibraryModal.open = true
  }
</script>

<div class="text-latte-text dark:text-macchiato-text">
  {#if isEmpty}
    <section
      class="no-libs relative isolate flex min-h-[calc(100dvh-5.5rem)] w-full flex-col items-center justify-center overflow-x-clip px-6 py-16 text-center"
      aria-labelledby="no-libs-title"
    >
      <div
        class="no-libs__glow pointer-events-none absolute left-1/2 top-[42%] size-[min(36rem,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-green)_22%,transparent),transparent_68%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-green)_18%,transparent),transparent_68%)]"
        aria-hidden="true"
      ></div>
      <div
        class="no-libs__glow-2 pointer-events-none absolute left-[max(0%,12%)] top-[18%] size-56 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-mauve)_16%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-mauve)_12%,transparent),transparent_70%)]"
        aria-hidden="true"
      ></div>
      <div
        class="no-libs__glow-3 pointer-events-none absolute bottom-[14%] right-[max(0%,8%)] size-48 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-peach)_14%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-peach)_10%,transparent),transparent_70%)]"
        aria-hidden="true"
      ></div>

      <div class="no-libs__stack relative z-[1] mx-auto flex w-full max-w-3xl flex-col items-center">
        <div class="no-libs__mark relative select-none" aria-hidden="true">
          <div
            class="no-libs__badge relative flex size-[clamp(5.5rem,18vw,8.5rem)] items-center justify-center rounded-[1.75rem] bg-latte-mantle/80 text-latte-green shadow-sm ring-1 ring-latte-surface1/60 backdrop-blur-sm sm:rounded-[2rem] dark:bg-macchiato-mantle/80 dark:text-macchiato-green dark:ring-macchiato-surface1/60"
          >
            <Library class="size-[42%] opacity-90" strokeWidth={1.75} />
            <span
              class="no-libs__zero absolute right-4 top-3.5 block font-semibold leading-none tracking-tighter text-latte-text sm:right-5 sm:top-4 dark:text-macchiato-text"
              style="font-size: clamp(1.35rem, 4.5vw, 1.85rem);"
            >
              0
            </span>
          </div>
        </div>

        <h1
          id="no-libs-title"
          class="no-libs__title mt-6 text-2xl font-semibold tracking-tight text-latte-text sm:text-3xl dark:text-macchiato-text"
        >
          An empty shelf awaits
        </h1>

        <p
          class="no-libs__copy mt-3 max-w-md text-sm leading-relaxed text-latte-text/70 sm:text-base dark:text-macchiato-text/70"
        >
          This server has no libraries yet. (╥╯⌒╰╥๑) <br><br> Get started by creating a library and pointing it at your media folders.
        </p>

        <div class="no-libs__actions mt-9 flex flex-wrap items-center justify-center gap-3">
          <Button
            class="h-11 gap-2 bg-latte-green px-5 font-semibold text-latte-base hover:bg-latte-green/90 dark:bg-macchiato-green dark:text-macchiato-base dark:hover:bg-macchiato-green/90"
            onclick={openCreateLibrary}
          >
            <Plus class="size-4" strokeWidth={2.5} />
            Create a library
          </Button>
        </div>
      </div>
    </section>
  {:else}
    <ResumeHero series={resumeSeries} embedBreadcrumb />
    <WelcomeStrip
      title={userName}
      kindStats={stripKindStats}
      progressStats={stripProgressStats}
    />
    <HomeBookshelfSection
      {serverid}
      series={continueSeries}
      header="Continue Reading"
      loading={seriesLoading && continueSeries.length === 0}
      showLibraryName
      accent="green"
    />
    <HomeTagSection
      {serverid}
      tags={genresTags.genres}
      header="Genres"
      kind="genre"
    />
    <HomeBookshelfSection
      {serverid}
      series={addedSeries}
      header="Recently Added"
      loading={seriesLoading && addedSeries.length === 0}
      showLibraryName
      accent="sky"
    />
    <!-- <HomeBookshelfSection {serverid} series={series.score} header="Highest Scores" showLibraryName/> -->
    <HomeTagSection
      {serverid}
      tags={genresTags.tags}
      header="Tags"
      kind="tag"
    />
  {/if}
</div>

<style>
  .no-libs__glow {
    animation: no-libs-breathe 7s ease-in-out infinite;
  }

  .no-libs__glow-2 {
    animation: no-libs-drift 11s ease-in-out infinite;
  }

  .no-libs__glow-3 {
    animation: no-libs-drift 13s ease-in-out infinite reverse;
  }

  .no-libs__stack {
    animation: no-libs-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .no-libs__mark {
    animation: no-libs-float 5.5s ease-in-out infinite;
  }

  .no-libs__zero {
    animation: no-libs-wiggle 2.3s ease-in-out infinite;
    transform-origin: 50% 100%;
  }

  .no-libs__title,
  .no-libs__copy,
  .no-libs__actions {
    animation: no-libs-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .no-libs__title {
    animation-delay: 0.06s;
  }

  .no-libs__copy {
    animation-delay: 0.12s;
  }

  .no-libs__actions {
    animation-delay: 0.18s;
  }

  @keyframes no-libs-rise {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes no-libs-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes no-libs-breathe {
    0%,
    100% {
      opacity: 0.85;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.08);
    }
  }

  @keyframes no-libs-drift {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(12px, -10px);
    }
  }

  @keyframes no-libs-wiggle {
    0%,
    100% {
      transform: rotate(-2deg);
    }
    50% {
      transform: rotate(2.5deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .no-libs__glow,
    .no-libs__glow-2,
    .no-libs__glow-3,
    .no-libs__stack,
    .no-libs__mark,
    .no-libs__zero,
    .no-libs__title,
    .no-libs__copy,
    .no-libs__actions {
      animation: none;
    }
  }
</style>
